import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
  Divider,
  Stack,
  Tooltip,
  IconButton,
  Button,
} from '@mui/material';
import {
  AutoAwesome as AIToolkitIcon,
  SmartToy as AgentIcon,
  Hub as McpIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { AI_TOOLS, CATEGORIES } from './aiToolsData';
import { resolveToolStatus } from './toolStatus';

const darkGreenColor = '#1D4434';
const orangeColor = '#D74B00';
const upColor = '#1D4434';
const downColor = '#C62828';
const checkingColor = '#6B6B6B';

const categoryIcon = {
  agents: AgentIcon,
  mcp: McpIcon,
};

/**
 * A copyable command / code snippet.
 * @param {{ code: string }} props
 * @returns {JSX.Element}
 */
const CodeBlock = ({ code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        bgcolor: 'rgba(29, 68, 52, 0.06)',
        border: '1px solid rgba(29, 68, 52, 0.12)',
        borderRadius: 1,
        px: 1.5,
        py: 1,
        my: 0.75,
      }}
    >
      <Box
        component="code"
        sx={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: '0.8125rem',
          color: darkGreenColor,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          lineHeight: 1.5,
        }}
      >
        {code}
      </Box>
      <Tooltip title={copied ? 'Copied' : 'Copy'}>
        <IconButton size="small" onClick={handleCopy} sx={{ mt: -0.5 }}>
          {copied ? (
            <CheckIcon fontSize="small" sx={{ color: darkGreenColor }} />
          ) : (
            <CopyIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

/**
 * Colored badges for tool type and status. `statusInfo` is the resolveToolStatus
 * result: live (up/down/checking) for probed MCP servers, or a static badge.
 * @param {{ typeLabel: string, statusInfo: {variant:string,label:string,detail?:string} }} props
 * @returns {JSX.Element}
 */
const ToolChips = ({ typeLabel, statusInfo }) => {
  const variant = statusInfo?.variant || 'static';
  const statusChip = {
    up: (
      <Chip
        label={statusInfo.label}
        size="small"
        sx={{ bgcolor: upColor, color: '#fff', fontWeight: 600 }}
      />
    ),
    down: (
      <Tooltip title={statusInfo.detail || 'Unreachable'}>
        <Chip
          label={statusInfo.label}
          size="small"
          sx={{ bgcolor: downColor, color: '#fff', fontWeight: 600 }}
        />
      </Tooltip>
    ),
    checking: (
      <Chip
        label={statusInfo.label}
        size="small"
        variant="outlined"
        sx={{ borderColor: checkingColor, color: checkingColor, fontWeight: 500 }}
      />
    ),
    static: (
      <Chip
        label={statusInfo?.label}
        size="small"
        variant="outlined"
        sx={{ borderColor: 'rgba(215, 75, 0, 0.4)', color: orangeColor, fontWeight: 500 }}
      />
    ),
  }[variant];

  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      <Chip
        label={typeLabel}
        size="small"
        sx={{ bgcolor: 'rgba(29, 68, 52, 0.1)', color: darkGreenColor, fontWeight: 600 }}
      />
      {statusChip}
    </Stack>
  );
};

/**
 * Compact summary card for the Overview tab; clicking jumps to the tool's category tab.
 * @param {{ tool: object, statusInfo: object, onOpen: () => void }} props
 * @returns {JSX.Element}
 */
const OverviewCard = ({ tool, statusInfo, onOpen }) => (
  <Card
    elevation={2}
    sx={{
      height: '100%',
      borderRadius: 2,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 },
    }}
  >
    <CardActionArea onClick={onOpen} sx={{ height: '100%', alignItems: 'stretch' }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: darkGreenColor, mb: 1 }}>
          {tool.name}
        </Typography>
        <ToolChips typeLabel={tool.typeLabel} statusInfo={statusInfo} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, flexGrow: 1 }}>
          {tool.oneLiner}
        </Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);

/**
 * A labelled detail field (label above content).
 * @param {{ label: string, children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
const DetailField = ({ label, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="overline"
      sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.06em' }}
    >
      {label}
    </Typography>
    <Box sx={{ mt: 0.25 }}>{children}</Box>
  </Box>
);

/**
 * Full detail panel for a single tool (used inside category tabs).
 * @param {{ tool: object, statusInfo: object }} props
 * @returns {JSX.Element}
 */
const ToolDetail = ({ tool, statusInfo }) => (
  <Paper elevation={2} sx={{ borderRadius: 2, p: { xs: 2, md: 3 }, mb: 3 }}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
      sx={{ mb: 1.5 }}
    >
      <Typography variant="h5" sx={{ fontWeight: 600, color: darkGreenColor }}>
        {tool.name}
      </Typography>
      <ToolChips typeLabel={tool.typeLabel} statusInfo={statusInfo} />
    </Stack>

    <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2 }}>
      {tool.oneLiner}
    </Typography>

    <Divider sx={{ mb: 2 }} />

    <DetailField label="What it does">
      <Typography variant="body2" color="text.secondary">
        {tool.whatItDoes}
      </Typography>
    </DetailField>

    <DetailField label="How to use">
      <Stack spacing={0.5}>
        {tool.howToUse.map((step, idx) => (
          <Box key={idx}>
            {step.text && (
              <Typography variant="body2" color="text.secondary">
                {step.text}
              </Typography>
            )}
            {step.code && <CodeBlock code={step.code} />}
          </Box>
        ))}
      </Stack>
    </DetailField>

    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <DetailField label="Tech">
          <Typography variant="body2" color="text.secondary">
            {tool.tech}
          </Typography>
        </DetailField>
      </Grid>
      <Grid item xs={12} md={6}>
        <DetailField label="Integrations">
          <Typography variant="body2" color="text.secondary">
            {tool.integrations}
          </Typography>
        </DetailField>
      </Grid>
    </Grid>

  </Paper>
);

/**
 * AI Toolkit view — Overview tab plus one tab per category.
 * @returns {JSX.Element}
 */
const AIToolkitView = () => {
  const [tab, setTab] = useState(0);

  const [health, setHealth] = useState({ statuses: {}, loading: true, error: false, checkedAt: null });

  const loadHealth = useCallback(async () => {
    setHealth((h) => ({ ...h, loading: true }));
    try {
      const res = await fetch('/api/ai-toolkit/health');
      const data = await res.json();
      const statuses = data.servers || {};
      setHealth({
        statuses,
        loading: false,
        error: Object.keys(statuses).length === 0,
        checkedAt: data.checkedAt || null,
      });
    } catch (e) {
      setHealth({ statuses: {}, loading: false, error: true, checkedAt: null });
    }
  }, []);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  const statusInfoFor = (tool) =>
    resolveToolStatus(tool, health.statuses, { loading: health.loading, error: health.error });

  // Tab 0 = Overview; tabs 1..n map to CATEGORIES.
  const openCategoryTab = (categoryKey) => {
    const idx = CATEGORIES.findIndex((c) => c.key === categoryKey);
    if (idx >= 0) setTab(idx + 1);
  };

  const toolsByCategory = (key) => AI_TOOLS.filter((t) => t.category === key);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <AIToolkitIcon sx={{ color: darkGreenColor }} />
        <Typography variant="h4" sx={{ fontWeight: 600, color: darkGreenColor }}>
          AI Toolkit
        </Typography>
      </Stack>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          Internal AI tooling built by the team — what each tool does and how to connect it in Claude Code and Cursor.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {health.error
              ? 'Live status unavailable — showing last known state.'
              : health.checkedAt
              ? `Last checked ${new Date(health.checkedAt).toLocaleTimeString()}`
              : 'Checking status…'}
          </Typography>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadHealth}
            disabled={health.loading}
            sx={{ textTransform: 'none', color: darkGreenColor }}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Paper elevation={1} sx={{ borderRadius: 2, mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="standard"
          sx={{
            pl: 2,
            // Remove the theme's horizontal tab padding so each tab cell is the
            // width of its text — this makes the indicator underline hug the
            // label instead of spanning the padded cell. Tabs are spaced with
            // right margin (which offsetLeft accounts for) rather than a flex
            // `gap`, because CSS gap breaks MUI's indicator position/width math.
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 0,
              minHeight: 48,
              px: 0,
              mr: 4,
            },
            '& .MuiTab-root:last-of-type': { mr: 0 },
            '& .Mui-selected': { color: `${orangeColor} !important` },
            '& .MuiTabs-indicator': { backgroundColor: orangeColor },
          }}
        >
          <Tab label="Overview" />
          {CATEGORIES.map((c) => (
            <Tab key={c.key} label={c.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Overview tab */}
      {tab === 0 && (
        <Box>
          {CATEGORIES.map((category) => {
            const tools = toolsByCategory(category.key);
            if (tools.length === 0) return null;
            const Icon = categoryIcon[category.key] || AIToolkitIcon;
            return (
              <Box key={category.key} sx={{ mb: 4 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Icon fontSize="small" sx={{ color: darkGreenColor }} />
                  <Typography
                    variant="overline"
                    sx={{ fontWeight: 700, letterSpacing: '0.08em', color: darkGreenColor }}
                  >
                    {category.label}
                  </Typography>
                </Stack>
                <Grid container spacing={2}>
                  {tools.map((tool) => (
                    <Grid item xs={12} sm={6} md={4} key={tool.id}>
                      <OverviewCard
                        tool={tool}
                        statusInfo={statusInfoFor(tool)}
                        onOpen={() => openCategoryTab(category.key)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Category tabs */}
      {CATEGORIES.map((category, idx) =>
        tab === idx + 1 ? (
          <Box key={category.key}>
            {toolsByCategory(category.key).map((tool) => (
              <ToolDetail key={tool.id} tool={tool} statusInfo={statusInfoFor(tool)} />
            ))}
          </Box>
        ) : null
      )}
    </Box>
  );
};

export default AIToolkitView;
